(() => {
  'use strict';

  const SELECTOR = '[data-free-shipping-progress]';
  const TEXT_SELECTOR = '[data-free-shipping-text]';
  const FILL_SELECTOR = '[data-free-shipping-fill]';
  const TRACK_SELECTOR = '[data-free-shipping-track]';

  // Unter 5,00 € Restbetrag wird "Nur noch ..." statt "Noch ..." angezeigt.
  const LOW_REMAINDER_CENTS = 500;

  let refreshTimer = null;
  let requestInFlight = null;
  let refreshQueued = false;

  /*
   * Shopify empfiehlt locale-aware Ajax-URLs.
   * Beispiel:
   * /cart.js
   * /de/cart.js
   */
  const cartUrl = () => {
    const root = window.Shopify?.routes?.root || '/';
    return `${root}cart.js`;
  };

  /*
   * Cent -> deutsches Euro-Format
   * 2501 -> 25,01 €
   */
  const formatEuro = (cents) =>
    new Intl.NumberFormat('de-DE', {
      style: 'currency',
      currency: 'EUR',
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    }).format(cents / 100);

  /*
   * Erkennt Änderungen am Shopify-Warenkorb.
   */
  const isCartMutationUrl = (url = '') => {
    const value = String(url);

    return /\/cart\/(add|change|update|clear)(\.js)?(?:\?|$)/.test(value);
  };

  const getWidgets = () =>
    Array.from(document.querySelectorAll(SELECTOR));

  /*
   * Fortschrittsbalken anhand aktueller Cart-Daten aktualisieren.
   */
  function render(cart) {
    const widgets = getWidgets();

    if (!widgets.length) return;

    const total = Math.max(
      0,
      Number(cart?.total_price) || 0
    );

    const itemCount = Math.max(
      0,
      Number(cart?.item_count) || 0
    );

    widgets.forEach((widget) => {
      const threshold = Number(widget.dataset.threshold);

      const text = widget.querySelector(TEXT_SELECTOR);
      const fill = widget.querySelector(FILL_SELECTOR);
      const track = widget.querySelector(TRACK_SELECTOR);

      if (
        !Number.isFinite(threshold) ||
        threshold <= 0 ||
        !text ||
        !fill
      ) {
        return;
      }

      /*
       * Bei leerem Warenkorb wird der Balken ausgeblendet.
       */
      if (itemCount === 0) {
        widget.hidden = true;
        return;
      }

      const remaining = Math.max(
        0,
        threshold - total
      );

      const percent = Math.min(
        100,
        Math.max(
          0,
          (total / threshold) * 100
        )
      );

      /*
       * Text bestimmen.
       */
      if (total >= threshold) {
        text.textContent =
          'Kostenloser Versand erreicht! 🎉';
      } else if (remaining <= LOW_REMAINDER_CENTS) {
        text.textContent =
          `Nur noch ${formatEuro(remaining)} bis zum kostenlosen Versand!`;
      } else {
        text.textContent =
          `Noch ${formatEuro(remaining)} bis zum kostenlosen Versand!`;
      }

      /*
       * Balken aktualisieren.
       * Maximal 100 %.
       */
      fill.style.width = `${percent}%`;

      if (track) {
        track.setAttribute(
          'aria-valuenow',
          String(Math.round(percent))
        );
      }

      widget.hidden = false;
    });
  }

  /*
   * Aktuellen Warenkorb von Shopify laden.
   */
  async function refresh() {
    if (!getWidgets().length) return;

    /*
     * Keine parallelen /cart.js-Anfragen erzeugen.
     */
    if (requestInFlight) {
      refreshQueued = true;
      return requestInFlight;
    }

    requestInFlight = fetch(cartUrl(), {
      method: 'GET',
      headers: {
        Accept: 'application/json',
      },
      credentials: 'same-origin',
      cache: 'no-store',
    })
      .then((response) => {
        if (!response.ok) {
          throw new Error(
            `Cart request failed: ${response.status}`
          );
        }

        return response.json();
      })
      .then(render)
      .catch((error) => {
        console.warn(
          '[Free shipping progress]',
          error
        );
      })
      .finally(() => {
        requestInFlight = null;

        if (refreshQueued) {
          refreshQueued = false;
          scheduleRefresh(0);
        }
      });

    return requestInFlight;
  }

  /*
   * Mehrere unmittelbar aufeinander folgende Änderungen
   * werden zu einer einzigen Aktualisierung zusammengefasst.
   */
  function scheduleRefresh(delay = 80) {
    window.clearTimeout(refreshTimer);

    refreshTimer = window.setTimeout(
      refresh,
      delay
    );
  }

  /*
   * Erkennt Themes, die fetch() für den AJAX-Warenkorb verwenden.
   */
  function patchFetch() {
    if (
      window.__freeShippingProgressFetchPatched ||
      typeof window.fetch !== 'function'
    ) {
      return;
    }

    const originalFetch =
      window.fetch.bind(window);

    window.fetch = async (...args) => {
      const response =
        await originalFetch(...args);

      try {
        const input = args[0];
        const options = args[1] || {};

        const url =
          typeof input === 'string'
            ? input
            : input?.url || '';

        const method = String(
          options.method ||
          input?.method ||
          'GET'
        ).toUpperCase();

        if (
          response.ok &&
          method !== 'GET' &&
          isCartMutationUrl(url)
        ) {
          scheduleRefresh(40);
        }
      } catch (_) {
        /*
         * Fehler im Beobachter dürfen andere
         * Theme-Skripte niemals blockieren.
         */
      }

      return response;
    };

    window.__freeShippingProgressFetchPatched =
      true;
  }

  /*
   * Fallback für Themes, die XMLHttpRequest statt fetch verwenden.
   */
  function patchXHR() {
    if (
      window.__freeShippingProgressXHRPatched ||
      typeof window.XMLHttpRequest === 'undefined'
    ) {
      return;
    }

    const originalOpen =
      XMLHttpRequest.prototype.open;

    const originalSend =
      XMLHttpRequest.prototype.send;

    XMLHttpRequest.prototype.open =
      function (method, url, ...rest) {
        this.__freeShippingProgressMethod =
          String(
            method || 'GET'
          ).toUpperCase();

        this.__freeShippingProgressUrl =
          String(url || '');

        return originalOpen.call(
          this,
          method,
          url,
          ...rest
        );
      };

    XMLHttpRequest.prototype.send =
      function (...args) {
        if (
          this.__freeShippingProgressMethod !== 'GET' &&
          isCartMutationUrl(
            this.__freeShippingProgressUrl
          )
        ) {
          this.addEventListener(
            'loadend',
            () => {
              if (
                this.status >= 200 &&
                this.status < 400
              ) {
                scheduleRefresh(40);
              }
            },
            { once: true }
          );
        }

        return originalSend.apply(
          this,
          args
        );
      };

    window.__freeShippingProgressXHRPatched =
      true;
  }

  /*
   * Manche Themes ersetzen den Cart Drawer komplett.
   * Dieser Observer erkennt, wenn unser Snippet neu
   * in den DOM eingesetzt wurde.
   */
  function observeReRenderedCart() {
    if (
      !document.body ||
      typeof MutationObserver === 'undefined'
    ) {
      return;
    }

    const observer =
      new MutationObserver((mutations) => {
        for (const mutation of mutations) {
          for (
            const node of mutation.addedNodes
          ) {
            if (!(node instanceof Element)) {
              continue;
            }

            if (
              node.matches?.(SELECTOR) ||
              node.querySelector?.(SELECTOR)
            ) {
              scheduleRefresh(0);
              return;
            }
          }
        }
      });

    observer.observe(
      document.body,
      {
        childList: true,
        subtree: true,
      }
    );
  }

  /*
   * Zusätzliche Fallbacks für unterschiedliche Themes.
   */
  function addThemeEventFallbacks() {
    [
      'cart:updated',
      'cart:refresh',
      'cart:change',
      'cart:update',
      'cart-drawer:open',
    ].forEach((eventName) =>
      document.addEventListener(
        eventName,
        () => scheduleRefresh(0)
      )
    );

    /*
     * Änderung der Menge.
     */
    document.addEventListener(
      'change',
      (event) => {
        const target = event.target;

        if (!(target instanceof Element)) {
          return;
        }

        if (
          target.closest(
            'cart-drawer, [id*="CartDrawer"], form[action*="/cart"]'
          )
        ) {
          scheduleRefresh(700);
        }
      },
      true
    );

    /*
     * Klassische Cart-Formulare.
     */
    document.addEventListener(
      'submit',
      (event) => {
        const form = event.target;

        if (
          !(form instanceof HTMLFormElement)
        ) {
          return;
        }

        if (
          form.action &&
          /\/cart\/(add|change|update)(?:\.js)?(?:\?|$)/.test(
            form.action
          )
        ) {
          scheduleRefresh(700);
        }
      },
      true
    );

    /*
     * Öffnen des Warenkorbs.
     */
    document.addEventListener(
      'click',
      (event) => {
        const target = event.target;

        if (!(target instanceof Element)) {
          return;
        }

        const trigger =
          target.closest(
            'a[href$="/cart"], a[href*="/cart?"], button[aria-controls*="cart" i], [data-cart-drawer-trigger], [data-cart-toggle]'
          );

        if (trigger) {
          scheduleRefresh(100);
        }
      },
      true
    );

    /*
     * Wichtig bei Vor-/Zurück-Navigation.
     */
    window.addEventListener(
      'pageshow',
      () => scheduleRefresh(0)
    );
  }

  function init() {
    patchFetch();
    patchXHR();
    observeReRenderedCart();
    addThemeEventFallbacks();

    scheduleRefresh(0);
  }

  if (
    document.readyState === 'loading'
  ) {
    document.addEventListener(
      'DOMContentLoaded',
      init,
      { once: true }
    );
  } else {
    init();
  }
})();